import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { IModulosEmpresa, ModulosEmpresaService, TModuloEmpresa, TODOS_MODULOS } from '../services/api/cadastros/ModulosEmpresaService';

// Qual módulo cada tela pertence, pela rota base (primeiro segmento da URL).
// Telas fora daqui (início, configurações da empresa) ficam sempre liberadas.
const MODULO_DA_ROTA: Record<string, TModuloEmpresa> = {
  venda: 'vendas',
  // As duas telas de comissão checam o sub-flag 'comissao', não 'vendas' — dá pra
  // desligar só elas sem desligar o PDV nem o gráfico de evolução de vendas.
  dashboardcomissao: 'comissao',
  dashboardvendas: 'comissao',
  dashboardevolucaovendas: 'vendas',
  relatoriovendas: 'vendas',

  parceiros: 'cadastros',
  transportadoras: 'cadastros',
  veiculos: 'cadastros',
  reboques: 'cadastros',
  operacoes: 'cadastros',
  contabancaria: 'cadastros',
  planocontas: 'cadastros',
  planodecontas: 'cadastros',
  usuarios: 'cadastros',

  entrada: 'estoque',
  produto: 'estoque',
  grupo: 'estoque',
  subgrupo: 'estoque',
  estoque: 'estoque',
  dashboardestoque: 'estoque',
  relatorioestoque: 'estoque',

  dashboardcontas: 'financeiro',
  contasreceber: 'financeiro',
  contaspagar: 'financeiro',
  faturamento: 'financeiro',
  conciliacao: 'financeiro',
  sessoesabertas: 'financeiro',
  entrecontas: 'financeiro',
  relatoriofinanceiro: 'financeiro',

  dashboardnotas: 'fiscal',
  nfe: 'fiscal',
  nfeentrada: 'fiscal',
  nfce: 'fiscal',
  nfedist: 'fiscal',
  relatoriosfiscal: 'fiscal',
  spedfiscal: 'fiscal',

  ordensservico: 'servicos',
  bens: 'servicos',
};

// Módulo do menu lateral ('/financeiro') -> chave do módulo ('financeiro').
export const moduloDoMenu = (pathModulo: string): TModuloEmpresa | undefined => {
  const chave = pathModulo.replace(/^\//, '');
  return chave in TODOS_MODULOS ? (chave as TModuloEmpresa) : undefined;
};

export const moduloDaRota = (pathname: string): TModuloEmpresa | undefined => {
  const base = pathname.split('/').filter(Boolean)[0] ?? '';
  return MODULO_DA_ROTA[base];
};

interface IModulosEmpresaContextData {
  // null enquanto ainda não carregou.
  modulos: IModulosEmpresa | null;
  // Rota (ou path de módulo do menu) liberada pra empresa? Enquanto carrega,
  // responde false pra módulos — evita mostrar por um instante o que depois some.
  rotaLiberada: (pathname: string) => boolean;
  moduloLiberado: (modulo: TModuloEmpresa) => boolean;
}

const ModulosEmpresaContext = createContext({} as IModulosEmpresaContextData);

export const useModulosEmpresa = () => useContext(ModulosEmpresaContext);

interface IModulosEmpresaProviderProps {
  children: React.ReactNode;
}

// Fica dentro do <Login> (via AppRoutes), então é montado de novo a cada
// login — trocar de usuário/empresa sempre busca os módulos da empresa nova.
export const ModulosEmpresaProvider: React.FC<IModulosEmpresaProviderProps> = ({ children }) => {
  const [modulos, setModulos] = useState<IModulosEmpresa | null>(null);

  useEffect(() => {
    ModulosEmpresaService.getModulos().then(resultado => {
      // Se a consulta falhar (backend fora do ar, coluna ainda não criada),
      // libera tudo em vez de travar o sistema pra empresa inteira.
      setModulos(resultado instanceof Error ? TODOS_MODULOS : resultado);
    });
  }, []);

  const valor = useMemo<IModulosEmpresaContextData>(() => {
    const moduloLiberado = (modulo: TModuloEmpresa) => !!modulos?.[modulo];
    return {
      modulos,
      moduloLiberado,
      rotaLiberada: (pathname: string) => {
        const modulo = moduloDaRota(pathname);
        return !modulo || moduloLiberado(modulo);
      },
    };
  }, [modulos]);

  return (
    <ModulosEmpresaContext.Provider value={valor}>
      {children}
    </ModulosEmpresaContext.Provider>
  );
};
